/**
 * The Keeper panel — the prototype's `kpanel`, on every screen.
 *
 * It sits in the corner of the whole app rather than on one page, because half the product
 * points at it: *"Ask"* on the Family Map, *"✦ Plan my week"* on the meal planner, and the
 * chips in the panel itself. Those were all dead ends while the panel did not exist.
 *
 * **Nothing here decides anything.** Keeper answers, and a person reads the answer. Where a
 * screen has already worked something out for itself — the meal planner fills the week from
 * the recipe box — the result is *said* here rather than computed here: `tell` puts a reply
 * in the log without going near the model, so what a family reads is what the product
 * actually did.
 */

import { askKeeper, keeperOpening } from '../api/keeper.js';
import { esc } from '../lib/dom.js';

let panel = null;
let log = null;
let opened = false;

/**
 * Put the panel and its button into the page, once.
 *
 * Called by the shell on every screen; the second call does nothing. A panel rebuilt on each
 * navigation would throw away the conversation somebody is in the middle of.
 */
export function mountKeeper(root = document.body) {
  if (panel) return panel;

  const holder = document.createElement('div');
  holder.innerHTML = `
    <button class="kfab" type="button" data-keeper-open>&#10022; Ask Keeper</button>
    <div class="kpanel" role="dialog" aria-label="Keeper">
      <div class="kp-head">
        <b>Keeper</b>
        <span style="font-size:.75rem;opacity:.8" data-keeper-tree></span>
        <button type="button" data-keeper-close aria-label="Close"
                style="margin-left:auto;background:none;border:0;color:#fff;font-size:1.2rem;
                       line-height:1;cursor:pointer">&times;</button>
      </div>
      <div class="kp-log" data-keeper-log></div>
      <p class="small" style="padding:0 16px 8px;margin:0;color:var(--moss)">
        Keeper reads your records and can still be wrong. Every figure on the screens behind
        this is worked out by the product itself, not by Keeper.
      </p>
      <div class="kp-quick" data-keeper-quick></div>
      <div class="kp-in">
        <input data-keeper-in placeholder="Ask about your family…"
               aria-label="Ask Keeper a question">
        <button class="btn plum sm" type="button" data-keeper-send>Send</button>
      </div>
    </div>
  `;

  const button = holder.querySelector('[data-keeper-open]');
  panel = holder.querySelector('.kpanel');
  log = panel.querySelector('[data-keeper-log]');

  root.append(button, panel);

  button.addEventListener('click', () => open());
  panel.querySelector('[data-keeper-close]').addEventListener('click', close);
  panel.querySelector('[data-keeper-send]').addEventListener('click', send);
  panel.querySelector('[data-keeper-in]').addEventListener('keydown', (event) => {
    if (event.key === 'Enter') send();
  });
  panel.querySelector('[data-keeper-quick]').addEventListener('click', (event) => {
    const chip = event.target.closest('button');
    if (chip) ask(chip.textContent.trim());
  });

  return panel;
}

/** Open the panel, loading the opening line the first time. */
export async function open() {
  if (!panel) mountKeeper();
  panel.classList.add('on');
  panel.querySelector('[data-keeper-in]').focus();

  if (opened) return;
  opened = true;

  try {
    const said = await keeperOpening();
    say('a', said.opening);
    panel.querySelector('[data-keeper-quick]').innerHTML = (said.quick || [])
      .map((one) => `<button class="btn ghost sm" type="button">${esc(one)}</button>`)
      .join('');
  } catch {
    // Keeper not answering is not a failure a family should meet — see `AskView`.
    say('a', 'I cannot reach the archive just now. Everything in it is still here.');
  }
}

export function close() {
  panel?.classList.remove('on');
}

/** One bubble. `u` is the person asking, `a` is Keeper. */
function say(who, words, id = '') {
  log.insertAdjacentHTML(
    'beforeend',
    `<div class="kmsg ${who}" ${id ? `data-bubble="${esc(id)}"` : ''}>
       <span class="b">${esc(words)}</span></div>`,
  );
  log.scrollTop = log.scrollHeight;
}

/**
 * Put a question and its answer in the panel without asking the model.
 *
 * **For the things the product already worked out.** The meal planner fills the week from the
 * recipe box, checking each dish against the household's rules — a real answer, computed from
 * real records. Sending that question to a model instead would replace something true with
 * something plausible.
 */
export async function tell(question, answer) {
  await open();
  say('u', question);
  say('a', answer);
}

async function send() {
  const box = panel.querySelector('[data-keeper-in]');
  const question = box.value.trim();
  if (!question) return;
  box.value = '';
  ask(question);
}

async function ask(question) {
  await open();
  say('u', question);

  const waiting = `wait-${Date.now()}`;
  log.insertAdjacentHTML(
    'beforeend',
    `<div class="kmsg a" data-bubble="${waiting}"><span class="b">
       <span class="dots"><i></i><i></i><i></i></span></span></div>`,
  );
  log.scrollTop = log.scrollHeight;

  let answer;
  try {
    answer = (await askKeeper(question)).answer;
  } catch (error) {
    answer =
      error.message ||
      'I could not reach the model just now. Nothing in your archive depends on me.';
  }

  const bubble = log.querySelector(`[data-bubble="${waiting}"] .b`);
  if (bubble) bubble.textContent = answer;
  log.scrollTop = log.scrollHeight;
}
